/**
 * Explains a sign-out that happened in another Admin tab.
 *
 * CRITICAL SLK-03-CROSS-TAB-NOTICE (Prism P60): Firebase ends the session in
 * every tab when the user signs out in one of them, so a tab with an unsaved
 * edit lands on a blank sign-in page. The signing-out tab writes a unique
 * localStorage value; browsers fire the `storage` event only in OTHER tabs of
 * the same origin, never in the writer, so only the affected tabs record the
 * notice. The sign-in page shows it once, then clears it. Storage failures
 * never block sign-out. Spec:
 * https://html.spec.whatwg.org/multipage/webstorage.html#the-storage-event
 */
export const ADMIN_SIGN_OUT_STORAGE_KEY = "hc-admin-sign-out";

let signedOutInAnotherTab = false;

/** Call before signing out so other open Admin tabs can explain it. */
export const announceAdminSignOut = (): void => {
  // This tab chose to sign out; its own sign-in page needs no notice.
  signedOutInAnotherTab = false;
  try {
    window.localStorage.setItem(
      ADMIN_SIGN_OUT_STORAGE_KEY,
      `${Date.now()}:${Math.random().toString(36).slice(2)}`,
    );
  } catch {
    // Blocked storage only loses the notice in other tabs.
  }
};

export const installAdminCrossTabSignOutListener = (
  target: Window,
): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === ADMIN_SIGN_OUT_STORAGE_KEY && event.newValue)
      signedOutInAnotherTab = true;
  };
  target.addEventListener("storage", onStorage);
  return () => target.removeEventListener("storage", onStorage);
};

export const hasAdminCrossTabSignOutNotice = (): boolean =>
  signedOutInAnotherTab;

export const clearAdminCrossTabSignOutNotice = (): void => {
  signedOutInAnotherTab = false;
};
